import React, { useState, useEffect, useRef } from 'react';
import AuthService from '../../services/authService';

const AdminMessaging = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('users'); // 'users' or 'conversation'
  const messagesEndRef = useRef(null);

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages when viewing a conversation
  useEffect(() => {
    if (!selectedConversation) return;

    const pollInterval = setInterval(() => {
      loadConversation(selectedConversation._id);
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [selectedConversation]);

  // Fetch all users
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/with-assignments`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const regularUsers = data.data.filter(user => user.role === 'user');
        setUsers(regularUsers);
        setFilteredUsers(regularUsers);
      }
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all conversations
  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  };

  // Load conversation with messages
  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/messages/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages);
        setView('conversation');
      }
    } catch (err) {
      setError('Error loading conversation: ' + err.message);
    }
  };

  // Start new conversation with user
  const startConversation = async (user) => {
    try {
      // Check if conversation already exists with THIS admin (not agent conversations)
      const existing = conversations.find(c => 
        c.user._id === user._id && 
        c.helper?.role === 'admin'
      );
      if (existing) {
        loadConversation(existing._id);
        return;
      }

      // Create new conversation
      const response = await fetch(`${API_URL}/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: user._id,
          subject: 'Message from Lauft Team'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Don't fetch all conversations again - just load the new one directly
        // This prevents duplicate notifications
        setConversations([...conversations, data.data]);
        loadConversation(data.data._id);
      } else {
        setError(data.error || 'Failed to create conversation');
      }
    } catch (err) {
      setError('Error starting conversation: ' + err.message);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    console.log('=== SENDING MESSAGE ===');
    console.log('Selected conversation ID:', selectedConversation._id);
    console.log('Selected conversation user:', selectedConversation.user);
    console.log('Message text:', messageText.trim());

    setIsSending(true);
    try {
      const url = `${API_URL}/messages/conversations/${selectedConversation._id}/messages`;
      console.log('POST URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({ content: messageText.trim() })
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        console.log('Message sent successfully, reloading conversation...');
        // Reload the conversation to get the correct message data
        // This prevents messages from going to the wrong conversation
        await loadConversation(selectedConversation._id);
        setMessageText('');
      } else {
        console.error('Failed to send message:', data);
        setError('Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Error sending message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(term.toLowerCase()) ||
        user.email.toLowerCase().includes(term.toLowerCase()) ||
        (user.phone && user.phone.includes(term))
      );
      setFilteredUsers(filtered);
    }
  };

  // Back to users list
  const backToUsers = () => {
    setView('users');
    setSelectedConversation(null);
    setMessages([]);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    fetchUsers();
    fetchConversations();
  }, []);

  // Users List View
  if (view === 'users') {
    return (
      <div className="content-container">
        <h2 className="section-title">Message Users</h2>

        {error && (
          <div className="alert alert-danger mb-4">
            {error}
          </div>
        )}

        {/* Search */}
        <div className="search-section mb-4">
          <div className="search-row">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="form-control search-input"
              />
            </div>
            <div className="stats-display">
              <span className="total-count">
                Showing {filteredUsers.length} of {users.length} users
              </span>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="form-section">
          <div className="form-section-body">
            {isLoading ? (
              <div className="loading-spinner">Loading users...</div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Contact</th>
                      <th>Last Activity</th>
                      <th>Assigned Agent</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => {
                      const userConversation = conversations.find(c => c.user._id === user._id);
                      const hasUnread = userConversation?.unreadCount > 0;
                      
                      return (
                        <tr key={user._id}>
                          <td>
                            <div className="user-info">
                              <div className="user-name">{user.name}</div>
                              <div className="user-id">ID: {user._id.slice(-6)}</div>
                            </div>
                          </td>
                          <td>
                            <div className="contact-info">
                              <div className="user-email">{user.email}</div>
                              <div className="user-phone">{user.phone || 'No phone'}</div>
                            </div>
                          </td>
                          <td>
                            <div className="activity-info">
                              <div className="likes-count">{user.likes?.length || 0} likes</div>
                              <div className="join-date">Joined {new Date(user.createdAt).toLocaleDateString()}</div>
                            </div>
                          </td>
                          <td>
                            {user.assignedAgent ? (
                              <div className="agent-info">
                                <div className="agent-name">{user.assignedAgent.name}</div>
                              </div>
                            ) : (
                              <span className="no-agent">Unassigned</span>
                            )}
                          </td>
                          <td>
                            <button
                              onClick={() => startConversation(user)}
                              className="btn btn-sm btn-primary"
                            >
                              {userConversation ? (hasUnread ? '💬 View (New)' : '💬 View Chat') : '✉️ Send Message'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <style jsx>{`
          .search-section {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #e9ecef;
          }
          
          .search-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
          }
          
          .search-box {
            flex: 1;
          }
          
          .search-input {
            font-size: 16px;
            padding: 12px;
          }
          
          .stats-display {
            white-space: nowrap;
          }
          
          .total-count {
            color: #666;
            font-weight: 500;
          }
          
          .user-info, .contact-info, .activity-info, .agent-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .user-name, .agent-name {
            font-weight: 600;
            color: #333;
          }
          
          .user-id, .user-phone, .join-date {
            font-size: 0.875rem;
            color: #666;
          }
          
          .user-email {
            color: #333;
          }
          
          .likes-count {
            font-weight: 500;
            color: #333;
          }
          
          .no-agent {
            color: #999;
            font-style: italic;
          }
          
          .btn-sm {
            padding: 8px 16px;
            font-size: 0.875rem;
          }
          
          .loading-spinner {
            text-align: center;
            padding: 40px;
            color: #666;
          }
        `}</style>
      </div>
    );
  }

  // Conversation View
  return (
    <div className="content-container">
      <div className="chat-header">
        <button onClick={backToUsers} className="btn btn-secondary">
          ← Back to Users
        </button>
        <div className="chat-header-info">
          <h3>{selectedConversation?.user?.name}</h3>
          <span className="user-email-small">{selectedConversation?.user?.email}</span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          {error}
        </div>
      )}

      <div className="chat-container">
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="no-messages">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              const isAdmin = message.senderRole === 'admin' || message.senderRole === 'agent';
              return (
                <div
                  key={message._id}
                  className={`message ${isAdmin ? 'message-admin' : 'message-user'}`}
                >
                  <div className="message-content">
                    <div className="message-text">{message.content}</div>
                    <div className="message-time">{formatTimestamp(message.createdAt)}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="message-input-container">
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Type your message..."
            className="message-input"
            rows="3"
            disabled={isSending}
          />
          <button
            onClick={sendMessage}
            disabled={!messageText.trim() || isSending}
            className="btn btn-primary send-button"
          >
            {isSending ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>

      <style jsx>{`
        .chat-header {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px;
          border-bottom: 2px solid #e9ecef;
          background: #f8f9fa;
          margin-bottom: 20px;
        }
        
        .chat-header-info h3 {
          margin: 0;
          font-size: 1.25rem;
          color: #333;
        }
        
        .user-email-small {
          font-size: 0.875rem;
          color: #666;
        }
        
        .chat-container {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 300px);
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
        }
        
        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .no-messages {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #999;
        }
        
        .message {
          display: flex;
          max-width: 70%;
        }
        
        .message-admin {
          align-self: flex-end;
        }
        
        .message-user {
          align-self: flex-start;
        }
        
        .message-content {
          padding: 12px 16px;
          border-radius: 12px;
          max-width: 100%;
        }
        
        .message-admin .message-content {
          background: #FFD700;
          color: #1a1a1a;
        }
        
        .message-user .message-content {
          background: #f1f3f4;
          color: #333;
        }
        
        .message-text {
          word-wrap: break-word;
          margin-bottom: 4px;
        }
        
        .message-time {
          font-size: 0.75rem;
          opacity: 0.7;
        }
        
        .message-input-container {
          padding: 20px;
          border-top: 1px solid #e9ecef;
          display: flex;
          gap: 12px;
          align-items: flex-end;
        }
        
        .message-input {
          flex: 1;
          padding: 12px;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          font-size: 14px;
          resize: none;
          font-family: inherit;
        }
        
        .message-input:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .send-button {
          padding: 12px 24px;
          white-space: nowrap;
        }
        
        .send-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default AdminMessaging;